import React from "react";
import { Toaster, ToastBar, type DefaultToastOptions, type Toast } from "react-hot-toast";

/** How an error toast is announced: interrupting, like showWarning
 *  (WCAG 4.1.3). Everything else keeps react-hot-toast's polite status. */
export const ERROR_ARIA = { role: "alert", "aria-live": "assertive" } as const;

/**
 * Errors announce assertively. Set when rendering, not in toastOptions:
 * react-hot-toast gives every toast its own polite ariaProps when it is
 * created, which win over the Toaster's per-type options, so an
 * `error.ariaProps` there would never apply to the ~200 toast.error calls.
 * An error raised with its own ariaProps keeps them.
 */
export const withErrorAria = (t: Toast): Toast =>
  t.type === "error" && t.ariaProps.role === "status"
    ? { ...t, ariaProps: ERROR_ARIA }
    : t;

const toastOptions = (theme: "light" | "dark"): DefaultToastOptions => ({
  duration: 4000,
  style: {
    background: theme === "dark" ? "#1e1e1e" : "#ffffff",
    color: theme === "dark" ? "#ffffff" : "#1a1a1a",
    borderRadius: 8,
    boxShadow: theme === "dark"
      ? "0 4px 12px rgba(0, 0, 0, 0.5)"
      : "0 4px 12px rgba(0, 0, 0, 0.15)",
    padding: "12px 16px",
    fontSize: "0.875rem",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  success: {
    style: {
      borderLeft: "4px solid #4caf50",
    },
    iconTheme: {
      primary: "#4caf50",
      secondary: theme === "dark" ? "#1e1e1e" : "#ffffff",
    },
  },
  error: {
    duration: 6000,
    style: {
      borderLeft: "4px solid #f44336",
    },
    iconTheme: {
      primary: "#f44336",
      secondary: theme === "dark" ? "#1e1e1e" : "#ffffff",
    },
  },
  loading: {
    style: {
      borderLeft: "4px solid #2196f3",
    },
  },
});

/** The app's one <Toaster> (App.tsx). */
export const AppToaster: React.FC<{ theme: "light" | "dark" }> = ({ theme }) => {
  const options = React.useMemo(() => toastOptions(theme), [theme]);
  return (
    <Toaster
      position="top-right"
      gutter={12}
      containerStyle={{
        top: 80, // Below header
      }}
      toastOptions={options}
    >
      {(t) => <ToastBar toast={withErrorAria(t)} />}
    </Toaster>
  );
};
